import { Injectable } from '@angular/core';
import { OrderStore } from '@modules/order/state/order-store';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ShellStore } from '@shared/store/shell/shell-store';
import { isLegacyRestorative, isResorative } from '@shared/utils/restorative-check-util';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HostPlatformService } from '@shared/services/host-platform.service';
import { AuthInfoQuery } from '@shared/store/authInfo/auth-info-query';
import { RoleTypeEnum } from '@shared/models/role-type';
import { ApplianceDetailsUtils } from '@modules/appliance-details/utils/appliance-details-utils';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { BiSessionInfo } from '@itero/rx-attachments/lib/models/analytics/BiSessionInfo';
import type { Maybe } from '@/types/globals';

@Injectable()
export class RxForLabFacade {
	caseTypeId$: Observable<Maybe<number>> = this.shellQuery.rx$.pipe(map(rx => rx?.Order?.CaseTypeId));
	procedureId$: Observable<Maybe<number>> = this.shellQuery.rx$.pipe(map(rx => rx?.Order?.ProcedureId));
	rx$ = this.shellQuery.rx$;
	isReadOnly$: Observable<boolean> = this.shellQuery.isReadOnly$;
	isRxloadedAndReady$: Observable<boolean> = this.isRxLoadedAndReady();
	isTeethDiagramAreaVisible$: Observable<boolean> = this.shellQuery.rx$.pipe(
		map(rx => {
			const isResto = !!rx?.Order?.CaseTypeId && isResorative({ caseTypeId: rx.Order.CaseTypeId });
			const isLegacyResto = !!rx?.Order?.CaseTypeId && isLegacyRestorative({ caseTypeId: rx.Order.CaseTypeId });

			return isResto || isLegacyResto;
		})
	);

	attachmentsRxId$ = this.shellQuery.attachmentsRxId$;
	companyId$ = this.shellQuery.companyId$;
	authInfo$ = this.authInfoQuery.authInfo$;
	contactId$: Observable<Maybe<number>> = this.shellQuery.contactId$;
	userRole$: Observable<RoleTypeEnum> = this.shellQuery.userRole$;
	appSettings$ = this.shellQuery.appSettings$;

	biSessionInfo$: Observable<BiSessionInfo> = this.biLoggerService.biSessionInfo$;

	isApplianceSectionVisibleForLabs$: Observable<boolean> = combineLatest([this.procedureId$, this.shellQuery.applianceDetails$]).pipe(
		map(([procedureId, applianceDetails]) => {
			if (!applianceDetails || !procedureId) {
				return false;
			}

			return ApplianceDetailsUtils.isApplianceDetailsEnabledForLabAndPrintRx(
				procedureId,
				applianceDetails.UpperJaw,
				applianceDetails.LowerJaw,
				applianceDetails.ApplianceType
			);
		})
	);

	get isProcedureFlow(): boolean {
		return this.shellQuery.isProcedureFlow;
	}

	get isIteroLab(): boolean {
		return this.hostPlatformService.isIteroLab;
	}

	constructor(
		private readonly shellStore: ShellStore,
		private readonly shellQuery: ShellQuery,
		private readonly orderStore: OrderStore,
		private readonly hostPlatformService: HostPlatformService,
		private readonly authInfoQuery: AuthInfoQuery,
		private readonly biLoggerService: BiLoggerService
	) {}

	updateIsReadOnly({ isReadOnly }: { isReadOnly: boolean }) {
		this.shellStore.update({ isReadOnly });
	}

	updateCaseTypeId({ caseTypeId }: { caseTypeId: number }) {
		this.orderStore.update({ caseType: { Id: caseTypeId, Name: '' } });
	}

	private isRxLoadedAndReady(): Observable<boolean> {
		return combineLatest([this.shellQuery.isLoading$, this.shellQuery.isRxPending$]).pipe(
			map(([isLoading, isRxPending]) => {
				return !isLoading && !isRxPending;
			})
		);
	}
}
